import React from 'react'
import { Card, makeStyles, Table, TableBody, TableCell, TableContainer, TableRow } from '@material-ui/core'
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd'
import { cn } from '../styles'

export const DraggableLayout = <T extends IDraggableZone>(props: {
  classes?: IDraggableClasses
  zones: T[][]
  onDragEnd: (result: DropResult) => void
  renderMetaSection?: (zone: T) => React.ReactNode
  renderPlacementHeader?: (zone: T) => React.ReactNode
  renderPlacementFooter?: (zone: T) => React.ReactNode
  renderCard?: (card: any) => React.ReactNode
}) => {
  const { draggableTableRow, draggableTableCell, tileCard, metaWrapper } = draggableStyles()
  const {
    classes,
    zones,
    onDragEnd,
    renderPlacementHeader,
    renderPlacementFooter,
    renderMetaSection = ((zone: T) => <p className={metaWrapper}>{ zone.title }</p>),
    renderCard = ((card: any) => <Card variant="outlined" className={tileCard}>{card.title}</Card>),
  } = props

  return <TableContainer className={cn(classes?.container)}>
    <DragDropContext onDragEnd={onDragEnd}>
      <Table size="small">
        <TableBody>
          { zones.map((row, i) =>
            <TableRow key={i} className={cn(draggableTableRow, classes?.row)}>
              { row.map((zone, j) =>
                <TableCell
                  key={j}
                  className={cn(
                    draggableTableCell,
                    classes?.cell,
                    {
                      [classes?.placementCell || '']: zone.cards !== undefined,
                      [classes?.metaCell || '']: zone.cards === undefined,
                    }
                  )}
                >
                  { zone.cards === undefined
                    ? renderMetaSection(zone)
                    : <>
                      { renderPlacementHeader ? renderPlacementHeader(zone) : null }
                      <DraggablePlacementZone
                        classes={classes}
                        zone={zone}
                        coords={[i,j]}
                        renderCard={renderCard}
                      />
                      { renderPlacementFooter ? renderPlacementFooter(zone) : null }
                    </>
                  }
                </TableCell>
              ) }
            </TableRow>
          ) }
        </TableBody>
      </Table>
    </DragDropContext>
  </TableContainer>
}

export const DraggablePlacementZone = ({
  classes,
  zone,
  coords,
  renderCard,
}: {
  classes?: IDraggableClasses
  zone: IDraggableZone
  coords: [number, number]
  renderCard: (card: any) => React.ReactNode
}) => {
  const { placementList, placementItem } = draggableStyles()
  const { cards } = zone

  const getListStyle = (isDraggingOver: any) => ({
    background: isDraggingOver ? 'lightblue' : undefined,
  })

  const getItemStyle = (isDragging: any, draggableStyle: any) => ({
    background: isDragging ? 'lightgreen' : undefined, // change background colour if dragging
    ...draggableStyle, // styles we need to apply on draggables
  })

  if (cards === undefined) { return null }
  return <Droppable droppableId={coords.join()}>
    {(provided, snapshot) =>
      <ul
        className={cn(placementList, classes?.placementList || '')}
        ref={provided.innerRef}
        style={getListStyle(snapshot.isDraggingOver)}
      >
        { cards.map((card, k) =>
          <Draggable key={k} draggableId={`${zone.id}-${k}`} index={k}>
            {(providedInner, snapshotInner) =>
              <li
                ref={providedInner.innerRef}
                className={cn(placementItem, classes?.placementItem)}
                {...providedInner.draggableProps}
                {...providedInner.dragHandleProps}
                style={getItemStyle(snapshotInner.isDragging, providedInner.draggableProps.style)}
              >
                { renderCard(card) }
              </li>
            }
          </Draggable>
        )}
        {provided.placeholder}
      </ul>
    }
  </Droppable>
}

/**
 * From the result of a drag operation, get a delta of the desired changes
 * @param result A DropResult
 * @param zones the current layout state or rows and columns
 */
export function getDragResultDelta<T extends IDraggableZone>(result: DropResult, zones: T[][]) {
  const { source, destination } = result
  if (source && destination) {
    const sourcePosition: DraggableCardAddress = source.droppableId.split(',').map((str) => parseInt(str)).concat([source.index]) as any
    const targetPosition: DraggableCardAddress = destination.droppableId.split(',').map((str) => parseInt(str)).concat([destination.index]) as any
    const sourceZone = zones[sourcePosition[0]][sourcePosition[1]]
    const targetZone = zones[targetPosition[0]][targetPosition[1]]
    const sourceCard = sourceZone && sourceZone.cards ? sourceZone.cards[source.index] : null
    return {
      source,
      sourceAddress: sourcePosition,
      sourceZone,
      sourceCard,
      target: destination,
      targetAddress: targetPosition,
      targetZone,
    }
  } else {
    return {
      source: null,
      sourceAddress: null,
      sourceZone: null,
      sourceCard: null,
      target: null,
      targetAddress: null,
      targetZone: null,
    }
  }
}

/**
 * Given the current state, return the new state after a card is moved
 * either removed from a source address, added to a target address, or both
 * @param zones A current state
 * @param op the card, and source/target to move it to/from
 */
export function updateAfterDragResult<T extends IDraggableZone>(
  zones: T[][],
  { sourceAddress, targetAddress, card }: { sourceAddress?: DraggableCardAddress, targetAddress?: DraggableCardAddress, card: any }
): T[][] {
  return zones.map((row, i) => {
    if (sourceAddress && sourceAddress[0] === i) {
      const zone = row[sourceAddress[1]]
      if (zone.cards) {
        zone.cards = zone.cards.filter((c, j) => j !== sourceAddress[2])
      }
    }

    if (targetAddress && targetAddress[0] === i) {
      const zone = row[targetAddress[1]]
      if (!zone.cards) { zone.cards = [] }
      zone.cards.splice(targetAddress[2], 0, card)
    }

    return row
  })
}

const draggableStyles = makeStyles({
  draggableTableRow: {
    verticalAlign: 'top',
  },
  draggableTableCell: {
    position: 'relative',
    height: '100%',
    padding: 0,
  },
  placementList: {
    listStyleType: 'none',
    padding: 6,
    margin: 0,
    height: '100%',
    paddingTop: 6,
    paddingBottom: 32,
    width: 250,
  },
  placementItem: {
    // margin: '4px 0',
    userSelect: 'none',
    // padding: grid * 2,
    margin: '0 0 8px 0',
  },
  tileWrapper: {
    display: 'block',
    margin: '2px 0',
    textDecoration: 'none',
  },
  metaWrapper: {
    padding: 6,
  },
  tileCard: {
    // minWidth: 300,
    padding: '6px 12px',
  },
})

export type DraggableCardAddress = [number, number, number] // Row, Col, Index of Card

export interface IDraggableZone {
  id: string
  title?: string
  cards?: any[]
  [key: string]: any
}

export interface IDraggableClasses {
  container?: string
  row?: string
  cell?: string
  metaCell?: string
  placementCell?: string
  placementList?: string
  placementItem?: string
}
